// Starshower (plasma rain) — custom timed event (custom-events-v1 §E3).
// Burning plasma lanes sweep diagonally across the arena from a seeded edge:
// 4–6 parallel lanes at a seeded 45°-family angle, each telegraphing for
// 1.0 s before firing a bolt stream down the lane for ~0.8 s. Lane telegraph
// markers are spaced so the player can thread the gaps.
//
// Mechanics (spec):
// - Bolts damage the player (10, mercy-gated — same gate + 'player_hit'
//   shape as space-storm firebolts) and enemies (20, same damage path as
//   meteor-shower impacts: direct hp loss, 'hit' event, and on death the
//   meteor-style 'death' + xp mote + release — NOT killEnemy).
// - An enemy takes at most one bolt hit per 8 ticks (throttle map); a bolt
//   stream is sustained area denial, not an instant delete. The player is
//   throttled by the standard mercy window.
//
// Determinism (GDD Appendix A §A.1): lane count, angle, and offsets are
// rolled in start() on a private mulberry32 stream reseeded from
// world.runSeed; the whole telegraph/fire timeline derives from the start
// tick. No Math.random, no wall-clock.
//
// Perf: bolts live in a preallocated scratch list (MAX_BOLTS) compacted in
// place (meteor-shower pattern); damage is a single enemy-pool pass against
// the live bolts, never a per-bolt pool scan.
//
// Visuals (wired by the parent, not here): 'hazard'
//   { event: 'starshower', phase: 'telegraph', lane: { a, b }, fire_tick }
//   { event: 'starshower', phase: 'fire', lane: { a, b } }
// Suggested look: glowing lane guides during the telegraph, then bright
// plasma bolts streaking down each lane.

import type { Enemy, EnemyDef, Vec2 } from '../../types.js';
import type { WorldState } from '../state.js';
import type { GameEventDef } from '../events-manager.js';
import { RngStream } from '../rng.js';
import { wrapArena, wrappedDelta } from '../collision.js';
import { applyDamageToPlayer } from '../combat.js';
import { foldIntoOldestMote } from '../kill.js';
import { cfg } from '../../config.js';
import enemiesJson from '../../content/enemies.json';

const ENEMY_DEFS = (enemiesJson as { version: string; entries: EnemyDef[] }).entries;

/** Event lifetime: 8 s at 60 Hz. */
const EVENT_DURATION_TICKS = 8 * 60;
/** First lane telegraphs 0.5 s after the event starts. */
const FIRST_TELEGRAPH_TICKS = 30;
/** Stagger between consecutive lane telegraphs (0.5 s). */
const LANE_STAGGER_TICKS = 30;
/** Telegraph duration: 1.0 s before the lane starts firing. */
const TELEGRAPH_TICKS = 60;
/** Fire window per lane: ~0.8 s of bolt stream. */
const FIRE_WINDOW_TICKS = 48;
/** A bolt leaves the lane's entry edge every 6 ticks (12 u spacing). */
const BOLT_INTERVAL_TICKS = 6;
/** Bolt speed: 120 u/s → 2 u per tick. */
const BOLT_SPEED = 120;
const BOLT_STEP = BOLT_SPEED / 60;
/** Bolt travel lifetime: 40 ticks = 80 u down the lane before burning out. */
const BOLT_TTL_TICKS = 40;
/** Bolt hit radius (vs entity hitbox). */
const BOLT_RADIUS = 1.5;
/** Damage: 10 to the player (mercy-gated), 20 to enemies. */
const PLAYER_DAMAGE = 10;
const ENEMY_DAMAGE = 20;
/** Per-enemy bolt-hit throttle (0.13 s): one hit per enemy per window. */
const ENEMY_HIT_THROTTLE_TICKS = 8;
/** Lane geometry: 4–6 parallel lanes, 9 u apart, diagonals only. */
const MIN_LANES = 4;
const LANE_COUNT_SPAN = 3; // next_int(3) → 0..2 added to the min
const LANE_SPACING = 9;
/** Clip lanes to the arena square with this margin inside the walls. */
const CLIP_MARGIN = 1;
/** Live-bolt scratch capacity: (TTL/interval + 1) per lane × max lanes. */
const MAX_BOLTS = 6 * (Math.ceil(BOLT_TTL_TICKS / BOLT_INTERVAL_TICKS) + 1) + 8;
/** Private stream salt: keeps lane rolls independent of every other system. */
const STARSHOWER_STREAM_SALT = 0x57A25EED;

const CLIP_HALF = 100 - CLIP_MARGIN;

interface Lane {
  /** Entry point on the arena boundary (bolts spawn here). */
  ax: number;
  ay: number;
  /** Exit point (telegraph segment end). */
  bx: number;
  by: number;
  /** Unit direction down the lane. */
  dx: number;
  dy: number;
  telegraphTick: number;
  fireStartTick: number;
  fireEndTick: number;
}

interface Bolt {
  x: number;
  y: number;
  dx: number;
  dy: number;
  expireTick: number;
}

function spawnXpMote(world: WorldState, pos: Vec2, value: number, tick: number): void {
  const p = world.pickups.acquire();
  if (!p) {
    // Pool exhausted: fold the value into the oldest live mote (see kill.ts).
    foldIntoOldestMote(world, value);
    return;
  }
  p.pickup_kind = 'xp_mote';
  p.value = value;
  p.pos = { x: pos.x, y: pos.y };
  p.vel = { x: 0, y: 0 };
  p.hp = 1;
  p.max_hp = 1;
  p.alive = true;
  p.spawn_tick = tick;
  p.targetable = false;
  p.magnet = true;
  world.events.emit('spawn', { kind: 'pickup', pickup_kind: 'xp_mote', pos: p.pos, value }, tick);
}

/** XP mote value for a kill, mirroring enemy-ai's loot pass. */
function xpValueFor(defId: string): number {
  const def = ENEMY_DEFS.find((d) => d.id === defId);
  return def ? def.drops.xp_motes : 1;
}

/**
 * Create the Starshower event def. All mutable state lives in this closure
 * and is fully reset in start(), so the def instance is reusable across runs
 * and across multiple triggers within one run.
 */
export function createStarshowerEvent(): GameEventDef {
  let stream = new RngStream(0);
  // Preallocated lane/bolt scratch (no per-tick allocation in update()).
  const lanes: Lane[] = [];
  for (let i = 0; i < MIN_LANES + LANE_COUNT_SPAN - 1; i++) {
    lanes.push({ ax: 0, ay: 0, bx: 0, by: 0, dx: 0, dy: 0, telegraphTick: 0, fireStartTick: 0, fireEndTick: 0 });
  }
  let laneCount = 0;
  const bolts: Bolt[] = [];
  for (let i = 0; i < MAX_BOLTS; i++) bolts.push({ x: 0, y: 0, dx: 0, dy: 0, expireTick: 0 });
  let boltCount = 0;
  /** Enemy id → tick until which bolt hits are throttled. Reset per activation. */
  const hitThrottle = new Map<number, number>();
  /** Scratch victim list for the two-pass kill (releasing mutates the pool). */
  const victims: Enemy[] = [];

  /**
   * Clip the infinite lane line (through C, direction D) to the arena
   * square with the slab method; writes entry/exit into the lane. Diagonal
   * directions always cross the square, so this never fails for our angles.
   */
  function clipLane(lane: Lane, cx: number, cy: number, dx: number, dy: number): void {
    let t0 = -Infinity;
    let t1 = Infinity;
    // X slab.
    const txa = (-CLIP_HALF - cx) / dx;
    const txb = (CLIP_HALF - cx) / dx;
    t0 = Math.max(t0, Math.min(txa, txb));
    t1 = Math.min(t1, Math.max(txa, txb));
    // Y slab.
    const tya = (-CLIP_HALF - cy) / dy;
    const tyb = (CLIP_HALF - cy) / dy;
    t0 = Math.max(t0, Math.min(tya, tyb));
    t1 = Math.min(t1, Math.max(tya, tyb));
    lane.ax = cx + t0 * dx;
    lane.ay = cy + t0 * dy;
    lane.bx = cx + t1 * dx;
    lane.by = cy + t1 * dy;
    lane.dx = dx;
    lane.dy = dy;
  }

  /** Roll lane count/angle/offsets and the whole telegraph/fire timeline. */
  function planLanes(startTick: number): void {
    laneCount = MIN_LANES + stream.next_int(LANE_COUNT_SPAN);
    // Diagonal sweeps only: 45° + k·90° (spec: "sweep diagonally").
    const angle = Math.PI / 4 + stream.next_int(4) * (Math.PI / 2);
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const nx = -dy;
    const ny = dx;
    for (let i = 0; i < laneCount; i++) {
      const lane = lanes[i];
      const offset = (i - (laneCount - 1) / 2) * LANE_SPACING;
      clipLane(lane, nx * offset, ny * offset, dx, dy);
      lane.telegraphTick = startTick + FIRST_TELEGRAPH_TICKS + i * LANE_STAGGER_TICKS;
      lane.fireStartTick = lane.telegraphTick + TELEGRAPH_TICKS;
      lane.fireEndTick = lane.fireStartTick + FIRE_WINDOW_TICKS;
    }
  }

  /** One enemy-pool pass: every live bolt against every targetable enemy. */
  function damageEnemies(world: WorldState, tick: number): void {
    if (boltCount === 0) return;
    for (const e of world.enemies.alive()) {
      if (!e.targetable) continue; // still materializing: not fully there yet
      const immuneUntil = hitThrottle.get(e.id);
      if (immuneUntil !== undefined && immuneUntil > tick) continue;
      for (let i = 0; i < boltCount; i++) {
        const b = bolts[i];
        const d = wrappedDelta(e.pos, b);
        const r = BOLT_RADIUS + e.hitbox_radius;
        if (d.x * d.x + d.y * d.y > r * r) continue;
        hitThrottle.set(e.id, tick + ENEMY_HIT_THROTTLE_TICKS);
        e.hp -= ENEMY_DAMAGE;
        world.events.emit(
          'hit',
          {
            target_id: e.id,
            damage: ENEMY_DAMAGE,
            crit: false,
            weapon_id: 'starshower',
            pos: { x: e.pos.x, y: e.pos.y }
          },
          tick
        );
        if (e.hp <= 0) victims.push(e);
        break; // one bolt hit per enemy per tick is plenty
      }
    }
    if (victims.length === 0) return;
    // Same kill path as meteor-shower impacts: death event + xp mote, then
    // release (no killEnemy — no health/crate rolls, no splitter).
    for (const e of victims) {
      world.events.emit(
        'death',
        {
          entity_id: e.id,
          enemy_id: e.id,
          kind: 'enemy',
          def_id: e.def_id,
          pos: { x: e.pos.x, y: e.pos.y }
        },
        tick
      );
      spawnXpMote(world, e.pos, xpValueFor(e.def_id), tick);
      world.enemies.release(e);
    }
    victims.length = 0;
  }

  function damagePlayer(world: WorldState, tick: number): void {
    const player = world.player;
    if (!player.alive || boltCount === 0) return;
    for (let i = 0; i < boltCount; i++) {
      const b = bolts[i];
      const d = wrappedDelta(player.pos, b);
      const r = BOLT_RADIUS + player.hitbox_radius;
      if (d.x * d.x + d.y * d.y > r * r) continue;
      if (player.dash.iframes_until > tick || player.mercy_until > tick) return;
      applyDamageToPlayer(player, PLAYER_DAMAGE, tick);
      player.mercy_until = tick + Math.round(cfg.getNumber('player.mercy_window') * 60);
      world.events.emit(
        'player_hit',
        { damage: PLAYER_DAMAGE, source: 'starshower', pos: { x: player.pos.x, y: player.pos.y } },
        tick
      );
      return;
    }
  }

  return {
    id: 'starshower',
    name: 'Starshower',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      stream = new RngStream((world.runSeed ^ STARSHOWER_STREAM_SALT) >>> 0);
      planLanes(tick);
      boltCount = 0;
      hitThrottle.clear();
      victims.length = 0;
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'starshower', phase: 'start', danger: 2, duration: EVENT_DURATION_TICKS },
        tick
      );
    },

    update(world: WorldState, tick: number, _progress: number): void {
      // Lane schedule: telegraphs, fire-start markers, and bolt spawns are
      // all pure functions of the start()-planned timeline.
      for (let i = 0; i < laneCount; i++) {
        const lane = lanes[i];
        if (tick === lane.telegraphTick) {
          world.events.emit(
            'hazard',
            {
              event: 'starshower',
              phase: 'telegraph',
              lane: { a: { x: lane.ax, y: lane.ay }, b: { x: lane.bx, y: lane.by } },
              fire_tick: lane.fireStartTick
            },
            tick
          );
        }
        if (tick === lane.fireStartTick) {
          world.events.emit(
            'hazard',
            {
              event: 'starshower',
              phase: 'fire',
              lane: { a: { x: lane.ax, y: lane.ay }, b: { x: lane.bx, y: lane.by } }
            },
            tick
          );
        }
        if (
          tick >= lane.fireStartTick &&
          tick < lane.fireEndTick &&
          (tick - lane.fireStartTick) % BOLT_INTERVAL_TICKS === 0 &&
          boltCount < MAX_BOLTS
        ) {
          const b = bolts[boltCount++];
          b.x = lane.ax;
          b.y = lane.ay;
          b.dx = lane.dx;
          b.dy = lane.dy;
          b.expireTick = tick + BOLT_TTL_TICKS;
        }
      }

      // Advance live bolts along their lanes; compact the list in place
      // (spawn order preserved → deterministic resolution order). Bolt is
      // Vec2-shaped, so wrapArena mutates it in place — no allocation.
      let write = 0;
      for (let read = 0; read < boltCount; read++) {
        const b = bolts[read];
        if (tick >= b.expireTick) continue;
        b.x += b.dx * BOLT_STEP;
        b.y += b.dy * BOLT_STEP;
        wrapArena(b);
        bolts[write++] = b;
      }
      boltCount = write;

      damagePlayer(world, tick);
      damageEnemies(world, tick);
    },

    end(world: WorldState, tick: number): void {
      boltCount = 0;
      hitThrottle.clear();
      victims.length = 0;
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'starshower', phase: 'end', danger: 2 },
        tick
      );
    }
  };
}
